import { FileType } from '@citruscamps/citrus-client'
import { Editor as CoreEditor, JSONContent } from '@tiptap/core'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import React, { useEffect, useState } from 'react'
import { hideAll } from 'tippy.js'
import { ILinkOptions } from './components/EditorLinkBubbleMenu'
import { EditorMenuBar } from './components/EditorMenuBar'
import { ButtonAttributes } from './interfaces/ButtonAttributes'
import { ImageExtendedAttributes } from './interfaces/ImageExtendedAttributes'
import {
  DefaultEditorMenuExtension,
  EditorMenuExtension,
  generateExtensions,
} from './utils/generateExtensions'

export const isFileInsertableImage = (type: FileType) =>
  ['image/jpeg', 'image/jpg', 'image/png'].includes(type)

const isSingleEmptyParagraph = (jsonContent: JSONContent) =>
  jsonContent.content &&
  jsonContent.content.length === 1 &&
  jsonContent.content[0].type === 'paragraph' &&
  !jsonContent.content[0].text &&
  (jsonContent.content[0].content?.length === 0 ||
    jsonContent.content[0].content?.every((node) => node.type === 'text'))

class EditorChangeEvent implements Pick<React.SyntheticEvent<HTMLInputElement>, 'target'> {
  target: EventTarget & { name?: string; value?: string }
  constructor({ name, value }: { name?: string; value?: string } = {}) {
    this.target = new EventTarget()
    Object.defineProperty(this.target, 'name', { writable: false, value: name })
    Object.defineProperty(this.target, 'value', { writable: false, value })
  }
}

interface FileUploadResult extends ImageExtendedAttributes {
  type: FileType
}

export interface IRichTextEditorProps
  extends Omit<React.HTMLProps<HTMLElement>, 'value' | 'onChange'> {
  value?: string | JSONContent
  extensions?: EditorMenuExtension[]
  buttonOptions?: Partial<ButtonAttributes>
  linkOptions?: ILinkOptions
  hasAttachments?: boolean
  onChange?: (e: EditorChangeEvent, { editor }: { editor: CoreEditor }) => void
  onSelectOrDropFiles?: (file: File[]) => Promise<FileUploadResult[]>
  onSelectOrDropFilesFailed?: (error: Error) => void
}

export const RichTextEditor = ({
  value: content = '',
  extensions = DefaultEditorMenuExtension,
  name,
  ref,
  buttonOptions,
  linkOptions,
  hasAttachments,
  onChange: handleChange,
  onBlur: handleBlur,
  onFocus: handleFocus,
  onSelectOrDropFiles: handleSelectOrDropFiles,
  onSelectOrDropFilesFailed: handleSelectOrDropFilesFailed,
  ...props
}: IRichTextEditorProps) => {
  const hasEmailExtension = extensions.includes('email')
  const [hasFocus, setHasFocus] = useState<boolean>(false)
  const editor: Editor | null = useEditor({
    extensions: generateExtensions(extensions),
    content,
    editorProps: {
      attributes: {
        class: 'prose focus:outline-none mx-2',
      },
      handleDrop: (view, event, _slice, moved) => {
        event.preventDefault()
        if (!moved && event.dataTransfer && event.dataTransfer.files) {
          handleSelectOrDropFiles?.(Array.from(event.dataTransfer.files))
            .then((files) => {
              for (const file of files.filter((file) => isFileInsertableImage(file.type))) {
                const { schema } = view.state
                const coordinates = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                })
                const node = schema.nodes.image.create(file)
                if (!coordinates) return false
                const transaction = view.state.tr.insert(coordinates.pos, node)
                view.dispatch(transaction)
              }
            })
            .catch((err) => {
              handleSelectOrDropFilesFailed?.(err)
            })
          return true
        }
        return false
      },
    },
    editable: !props.disabled,
    onUpdate: ({ editor: ed }) => {
      const edJson = ed?.getJSON()
      const value = isSingleEmptyParagraph(edJson) ? ed?.getText() : ed?.getHTML()
      return handleChange?.(new EditorChangeEvent({ name, value }), { editor: ed })
    },
    onDestroy: () => {
      hideAll()
    },
  })

  useEffect(() => {
    if (editor && content.length === 0) {
      editor.commands.clearContent(true)
    }
  }, [content, editor])

  return (
    <div
      className={`form-control h-auto${hasFocus ? ' focused' : ''}${
        props.disabled ? ' disabled' : ''
      }`}
    >
      <EditorMenuBar
        editor={editor}
        disabled={props.disabled}
        buttonOptions={buttonOptions}
        linkOptions={linkOptions}
        hasAttachments={hasAttachments}
        hasEmailExtension={hasEmailExtension}
        onSelectFiles={async (selectedFiles) => {
          if (!selectedFiles) return []
          const allFiles = await handleSelectOrDropFiles?.(selectedFiles)
          return allFiles?.filter((file) => isFileInsertableImage(file.type)) || []
        }}
      />
      <EditorContent
        {...props}
        name={name}
        editor={editor}
        onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
          setHasFocus(false)
          return handleBlur?.({
            ...e,
            target: { ...e.target, name, value: editor?.getHTML() } as any,
          })
        }}
        onFocus={(e: React.FocusEvent<HTMLDivElement>) => {
          if (props.disabled) return
          setHasFocus(true)
          return handleFocus?.({
            ...e,
            target: { ...e.target, name, value: editor?.getHTML() } as any,
          })
        }}
      />
    </div>
  )
}
